import { Mic, Square } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "@/api/client";
import { useApiClient } from "@/api/ApiContext";
import {
  transcribeSpeech,
  usePreviewSpeechConfigQuery,
} from "@/api/previewSpeech";
import { useCapability } from "@/auth/SessionContext";
import {
  STT_MAX_BYTES,
  canRecord,
  startRecording,
  toWav16kMono,
  type Recording,
} from "@/lib/labRecorder";
import {
  VOICE_INPUT_UNAVAILABLE,
  joinDictation,
  recognitionCtor,
  speechTag,
  stopSpeaking,
  transcriptOf,
  type RecognitionLike,
} from "@/lib/labSpeech";
import { IconButton } from "@/ui/IconButton";

const ERROR_MESSAGES: Record<string, string> = {
  "not-allowed":
    "Microphone access was blocked. Allow it in the browser to dictate.",
  "service-not-allowed":
    "Microphone access was blocked. Allow it in the browser to dictate.",
  "language-not-supported": "This browser cannot dictate in that language.",
  "audio-capture": "No microphone was found.",
  network: "Voice input could not reach the speech service.",
};

const TENANT_STT_FAILED =
  "Tenant speech recognition failed; using the browser's.";
const TENANT_STT_DENIED =
  "Your account cannot use the tenant's speech recognition.";
const TENANT_STT_UNREADABLE =
  "The recording could not be transcribed. Try again.";
const RECORDING_TOO_LONG =
  "That recording is too long. Keep it under a minute.";
const MIC_BLOCKED =
  "Microphone access was blocked. Allow it in the browser to dictate.";

/**
 * Dictation into a text field with the browser's speech recognition. Interim
 * words go through `onText` as they arrive (typed text stays in front of
 * them); recognition stops on a second press or on silence. `onFinished`
 * tells whether anything was heard.
 *
 * With `projectId` and `previewRunRef` and a tenant that offers speech
 * recognition, the press records audio instead, and the second press sends it
 * to the tenant's recogniser (no interim words). A failure of that service
 * switches to the browser engine for the rest of the session.
 */
export function useDictation({
  language,
  getText,
  onText,
  onFinished,
  projectId = "",
  previewRunRef = "",
}: {
  projectId?: string;
  previewRunRef?: string;
  language: string | null | undefined;
  getText: () => string;
  onText: (text: string) => void;
  onFinished: (heard: boolean) => void;
}) {
  const Ctor = recognitionCtor();
  const client = useApiClient();
  const inspect = useCapability("preview.inspect");
  const create = useCapability("preview.create");
  const config = usePreviewSpeechConfigQuery(projectId, previewRunRef, {
    enabled: inspect.allowed && create.allowed,
  });
  const [tenantFailed, setTenantFailed] = useState(false);
  const tenant =
    !tenantFailed && config.data?.sttAvailable === true && canRecord();
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState<{ heard: boolean } | null>(null);
  const active = useRef<RecognitionLike | null>(null);
  const recording = useRef<Recording | null>(null);
  const starting = useRef(false);
  const handlers = useRef({ onText, onFinished });
  useEffect(() => {
    handlers.current = { onText, onFinished };
  });
  // The recognised words are in the field after this render; only then may the
  // caller send them.
  useEffect(() => {
    if (finished !== null) {
      handlers.current.onFinished(finished.heard);
    }
  }, [finished]);

  const stop = useCallback(() => active.current?.stop(), []);

  const startTenant = useCallback(async () => {
    stopSpeaking();
    setError(null);
    const base = getText();
    starting.current = true;
    let current: Recording;
    try {
      current = await startRecording();
    } catch (failure) {
      const name = (failure as { name?: string }).name;
      setError(
        name === "NotFoundError"
          ? ERROR_MESSAGES["audio-capture"]!
          : MIC_BLOCKED,
      );
      return;
    } finally {
      starting.current = false;
    }
    recording.current = current;
    setListening(true);
    try {
      const raw = await current.done;
      setListening(false);
      setTranscribing(true);
      const wav = await toWav16kMono(raw);
      if (wav.size > STT_MAX_BYTES) {
        setError(RECORDING_TOO_LONG);
        return;
      }
      const result = await transcribeSpeech(client, projectId, previewRunRef, {
        audio: wav,
        language: speechTag(language),
      });
      const spoken = result.text.trim();
      if (spoken !== "") {
        handlers.current.onText(joinDictation(base, spoken));
      }
      setFinished({ heard: spoken !== "" });
    } catch (failure) {
      if (recording.current !== current) {
        return; // cancelled on unmount
      }
      if (failure instanceof ApiError && failure.status === 403) {
        setError(TENANT_STT_DENIED);
      } else if (
        (failure instanceof ApiError &&
          [404, 502, 503].includes(failure.status)) ||
        failure instanceof TypeError
      ) {
        // The service is unreachable or lacks speech: the browser's engine
        // takes over for the rest of the session.
        setTenantFailed(true);
        setError(TENANT_STT_FAILED);
      } else {
        setError(TENANT_STT_UNREADABLE);
      }
    } finally {
      if (recording.current === current) {
        recording.current = null;
      }
      setListening(false);
      setTranscribing(false);
    }
  }, [client, getText, language, previewRunRef, projectId]);

  const start = useCallback(() => {
    if (Ctor === null || active.current !== null) {
      return;
    }
    stopSpeaking();
    setError(null);
    const base = getText();
    const recognition = new Ctor();
    let heard = false;
    recognition.lang = speechTag(language);
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      const spoken = transcriptOf(event);
      if (spoken !== "") {
        heard = true;
        handlers.current.onText(joinDictation(base, spoken));
      }
    };
    recognition.onerror = (event) => {
      const message = ERROR_MESSAGES[event.error];
      if (message !== undefined) {
        setError(message);
      } else if (event.error !== "no-speech" && event.error !== "aborted") {
        setError(`Voice input stopped (${event.error}).`);
      }
    };
    recognition.onend = () => {
      active.current = null;
      setListening(false);
      handlers.current.onFinished(heard);
    };
    active.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch {
      active.current = null;
      setError("Voice input could not start.");
    }
  }, [Ctor, getText, language]);

  // Leaving the page releases the microphone.
  useEffect(
    () => () => {
      active.current?.abort();
      const pending = recording.current;
      recording.current = null;
      pending?.cancel();
    },
    [],
  );

  return {
    supported: tenant || Ctor !== null,
    listening,
    transcribing,
    error,
    toggle: () => {
      if (tenant) {
        if (recording.current !== null) {
          recording.current.stop();
        } else if (!transcribing && !starting.current) {
          void startTenant();
        }
        return;
      }
      if (active.current === null) {
        start();
      } else {
        stop();
      }
    },
  };
}

export function MicButton({
  supported,
  listening,
  disabled,
  onClick,
}: {
  supported: boolean;
  listening: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  if (!supported) {
    return (
      <span title={VOICE_INPUT_UNAVAILABLE} className="inline-flex">
        <IconButton
          type="button"
          variant="outline"
          disabled
          showTooltip={false}
          label={VOICE_INPUT_UNAVAILABLE}
          icon={<Mic className="size-4" />}
        />
      </span>
    );
  }
  return (
    <IconButton
      type="button"
      variant={listening ? "primary" : "outline"}
      disabled={disabled && !listening}
      showTooltip={false}
      aria-pressed={listening}
      title={listening ? "Stop voice input" : "Dictate the patient's line"}
      label={listening ? "Stop voice input" : "Start voice input"}
      icon={
        listening ? <Square className="size-4" /> : <Mic className="size-4" />
      }
      onClick={onClick}
    />
  );
}
